import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Archive, ChevronDown, ChevronRight, ImageIcon, Package } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getImageUrl } from '../utils/imageUrl';
import { translateStatus } from '../i18n/translations';
import { hasExpress } from '../utils/express';
import { TrackingDialog } from '../components/TrackingDialog';
import { Button } from '../components/ui/button';

export const Archives = () => {
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [trackingDetailOpen, setTrackingDetailOpen] = useState(false);
  const [trackingDetailNumber, setTrackingDetailNumber] = useState('');
  const [expandedMonths, setExpandedMonths] = useState({});
  const [rate, setRate] = useState(25000);

  useEffect(() => {
    fetchArchives();
    fetchSettings();
  }, []);

  const fetchArchives = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/archives`,
        { withCredentials: true }
      );
      setOrders(response.data);
      const grouped = groupByMonth(response.data);
      if (grouped.length > 0) {
        setExpandedMonths({ [grouped[0].label]: true });
      }
    } catch (error) {
      console.error('Error fetching archives:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, { withCredentials: true });
      setRate(res.data.eur_vnd_rate);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const groupByMonth = (orderList) => {
    const groups = {};
    for (const o of orderList) {
      const label = o.month_label || 'Inconnu';
      if (!groups[label]) groups[label] = [];
      groups[label].push(o);
    }
    return Object.entries(groups).map(([label, items]) => ({ label, orders: items }));
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setDialogOpen(true);
  };

  const toggleMonth = (label) => {
    setExpandedMonths(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const monthGroups = groupByMonth(orders);

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Archives</h1>
          <p className="text-sm text-slate-600 mt-1">Commandes livrées regroupées par mois</p>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Archive size={20} className="text-slate-600" />
              <CardTitle className="text-lg font-medium">{orders.length} commande(s) livrée(s)</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : orders.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-500">Aucune commande archivée</p>
            ) : (
              <div className="space-y-3">
                {monthGroups.map((group) => (
                  <div key={group.label} data-testid="archive-month-group" className="border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      data-testid="toggle-archive-month"
                      className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                      onClick={() => toggleMonth(group.label)}
                    >
                      <div className="flex items-center gap-3">
                        {expandedMonths[group.label] ? <ChevronDown size={18} className="text-slate-500" /> : <ChevronRight size={18} className="text-slate-500" />}
                        <span className="font-semibold text-slate-900">{group.label}</span>
                      </div>
                      <Badge className="bg-slate-200 text-slate-700 border-0">{group.orders.length} commande(s)</Badge>
                    </button>
                    {expandedMonths[group.label] && (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-white">
                              <TableHead className="font-medium w-14"></TableHead>
                              <TableHead className="font-medium">{t('order_number')}</TableHead>
                              <TableHead className="font-medium">Client</TableHead>
                              <TableHead className="font-medium">Pays</TableHead>
                              <TableHead className="font-medium">Pièces</TableHead>
                              <TableHead className="font-medium">Date commande</TableHead>
                              <TableHead className="font-medium">Date livraison</TableHead>
                              <TableHead className="font-medium">Commercial</TableHead>
                              <TableHead className="font-medium text-right">Prix vente</TableHead>
                              <TableHead className="font-medium text-right">Marge</TableHead>
                              <TableHead className="font-medium">Société</TableHead>
                              <TableHead className="font-medium">Suivi</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {group.orders.map((order) => (
                              <TableRow
                                key={order.id}
                                data-testid="archive-row"
                                className="hover:bg-slate-50 cursor-pointer"
                                onClick={() => handleOrderClick(order)}
                              >
                                <TableCell>
                                  {order.images?.length > 0 ? (
                                    <img src={getImageUrl(order.images[0])} alt="" className="w-10 h-10 object-cover rounded border cursor-pointer" />
                                  ) : (
                                    <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center"><ImageIcon size={14} className="text-slate-400" /></div>
                                  )}
                                </TableCell>
                                <TableCell className="font-medium">{order.order_number}</TableCell>
                                <TableCell>{order.client_name} {order.client_firstname}</TableCell>
                                <TableCell>{order.client_country}</TableCell>
                                <TableCell>{order.num_pieces} {order.piece_type}</TableCell>
                                <TableCell className="text-sm">{new Date(order.order_date).toLocaleDateString('fr-FR')}</TableCell>
                                <TableCell className="text-sm">{new Date(order.delivery_date).toLocaleDateString('fr-FR')}</TableCell>
                                <TableCell className="text-sm">{order.commercial_name}</TableCell>
                                <TableCell className="text-right font-medium">{order.sale_price_eur?.toFixed(2)} &euro;</TableCell>
                                <TableCell className="text-right font-medium">{order.gross_margin?.toFixed(2)} &euro;</TableCell>
                                <TableCell>
                                  <Badge className="bg-slate-100 text-slate-800 border border-slate-200 text-xs">{order.company}</Badge>
                                </TableCell>
                                <TableCell>
                                  {order.tracking_number && (
                                    <Button size="sm" className="h-6 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={(e) => { e.stopPropagation(); setTrackingDetailNumber(order.tracking_number); setTrackingDetailOpen(true); }}>
                                      <Package size={12} /> SUIVI
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="archive-detail-dialog">
            <DialogHeader>
              <DialogTitle>Commande {selectedOrder?.order_number}</DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-slate-500">Client</Label>
                    <p className="font-medium">{selectedOrder.client_name} {selectedOrder.client_firstname}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Pays</Label>
                    <p className="font-medium">{selectedOrder.client_country}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Téléphone</Label>
                    <p>{selectedOrder.client_phone}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Email</Label>
                    <p>{selectedOrder.client_email}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Adresse</Label>
                    <p>{selectedOrder.client_address || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Société</Label>
                    <p className="font-medium">{selectedOrder.company}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Pièces</Label>
                    <p>{selectedOrder.num_pieces} {selectedOrder.piece_type}</p>
                    {selectedOrder.order_lines && selectedOrder.order_lines.length > 0 && (
                      <p className="text-xs text-slate-400 mt-0.5">{selectedOrder.order_lines.length} {t('product_lines')}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Urgence</Label>
                    <p>{selectedOrder.urgency}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Date commande</Label>
                    <p>{new Date(selectedOrder.order_date).toLocaleDateString('fr-FR')}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Livraison bureau</Label>
                    <p className="font-semibold">{selectedOrder.bureau_delivery_date ? new Date(selectedOrder.bureau_delivery_date).toLocaleDateString('fr-FR') : '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Livraison client</Label>
                    <p className="font-semibold">{selectedOrder.client_delivery_date ? new Date(selectedOrder.client_delivery_date).toLocaleDateString('fr-FR') : '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Statut</Label>
                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 uppercase">{translateStatus(selectedOrder.status, t)}</Badge>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Commercial</Label>
                    <p>{selectedOrder.commercial_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Commission</Label>
                    <p className="font-medium text-emerald-700">{selectedOrder.commercial_commission?.toFixed(2)} &euro;</p>
                  </div>
                  {selectedOrder.tracking_number && (
                    <div>
                      <Label className="text-xs text-slate-500">N° de suivi</Label>
                      <p>{selectedOrder.tracking_number}</p>
                    </div>
                  )}
                  {selectedOrder.tracking_link && (
                    <div>
                      <Label className="text-xs text-slate-500">Lien de suivi</Label>
                      <a href={selectedOrder.tracking_link} target="_blank" rel="noreferrer" className="text-blue-600 underline text-sm">{selectedOrder.tracking_link}</a>
                    </div>
                  )}
                </div>

                {/* Détail des pièces (lignes de commande) */}
                {selectedOrder.order_lines && selectedOrder.order_lines.length > 0 && (
                  <div data-testid="archive-order-lines-detail" className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">{t('product')}</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">{t('quantity')}</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">{t('unit_cost')}</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">{t('unit_price')}</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">{t('weight')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.order_lines.map((line, idx) => (
                          <tr key={`al-${line.product_name}-${idx}`} className="border-t border-slate-100">
                            <td className="px-3 py-2 font-medium text-slate-800">{line.product_name}</td>
                            <td className="px-3 py-2 text-right text-slate-700">{line.quantity}</td>
                            <td className="px-3 py-2 text-right text-slate-700">
                              {(line.unit_cost || 0).toFixed(2)} &euro;
                              <div className="text-[10px] text-slate-400">{((line.unit_cost || 0) * rate).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} &#8363;</div>
                            </td>
                            <td className="px-3 py-2 text-right text-slate-700">
                              {(line.unit_price || 0).toFixed(2)} &euro;
                              <div className="text-[10px] text-slate-400">{((line.unit_price || 0) * rate).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} &#8363;</div>
                            </td>
                            <td className="px-3 py-2 text-right text-slate-700">{(line.weight_kg || 0).toFixed(2)} kg</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* PROFIT ESTIMÉ - Green Box */}
                <div data-testid="archive-estimated-profit-box" className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-4 mt-2">
                  <h3 className="text-sm font-bold text-emerald-800 mb-3">{t('estimated_profit')}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-end">
                      <span className="text-emerald-700">{t('selling_price')}</span>
                      <div className="text-right">
                        <span className="font-bold text-emerald-900">{(selectedOrder.sale_price_eur || 0).toFixed(2)} &euro;</span>
                        <div className="text-[10px] text-slate-400">{((selectedOrder.sale_price_eur || 0) * rate).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} &#8363;</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-end text-red-700">
                      <span>- {t('product_cost_label')}</span>
                      <div className="text-right">
                        <span className="font-medium">-{(selectedOrder.material_cost || 0).toFixed(2)} &euro;</span>
                        <div className="text-[10px] text-slate-400">-{((selectedOrder.material_cost || 0) * rate).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} &#8363;</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-end text-red-700">
                      <span>- {t('transport_cost')}</span>
                      <div className="text-right">
                        <span className="font-medium">-{(selectedOrder.transport_cost || 0).toFixed(2)} &euro;</span>
                        <div className="text-[10px] text-slate-400">-{((selectedOrder.transport_cost || 0) * rate).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} &#8363;</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-end text-red-700">
                      <span>- {t('commission')}</span>
                      <div className="text-right">
                        <span className="font-medium">-{(selectedOrder.commercial_commission || 0).toFixed(2)} &euro;</span>
                        <div className="text-[10px] text-slate-400">-{((selectedOrder.commercial_commission || 0) * rate).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} &#8363;</div>
                      </div>
                    </div>
                    <div className="border-t border-emerald-300 pt-2 flex justify-between items-end">
                      <span className="font-bold text-emerald-900">= {t('estimated_profit')}</span>
                      <div className="text-right">
                        <span className="font-bold text-lg text-emerald-900">
                          {((selectedOrder.sale_price_eur || 0) - (selectedOrder.material_cost || 0) - (selectedOrder.transport_cost || 0) - (selectedOrder.commercial_commission || 0)).toFixed(2)} &euro;
                        </span>
                        <div className="text-[10px] text-slate-400">
                          {(((selectedOrder.sale_price_eur || 0) - (selectedOrder.material_cost || 0) - (selectedOrder.transport_cost || 0) - (selectedOrder.commercial_commission || 0)) * rate).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} &#8363;
                        </div>
                      </div>
                    </div>
                    {(() => {
                      const profit = (selectedOrder.sale_price_eur || 0) - (selectedOrder.material_cost || 0) - (selectedOrder.transport_cost || 0) - (selectedOrder.commercial_commission || 0);
                      const marginPct = (selectedOrder.sale_price_eur || 0) !== 0 ? (profit / (selectedOrder.sale_price_eur || 1)) * 100 : 0;
                      return (
                        <div data-testid="archive-margin-percent" className="flex justify-between items-center pt-1">
                          <span className="font-semibold text-emerald-800 text-xs uppercase">{t('margin')} %</span>
                          <span className={`font-bold text-sm ${marginPct >= 0 ? 'text-emerald-900' : 'text-red-600'}`}>{marginPct.toFixed(1)} %</span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <TrackingDialog
          open={trackingDetailOpen}
          onOpenChange={setTrackingDetailOpen}
          trackingNumber={trackingDetailNumber}
        />
      </div>
    </Layout>
  );
};
